import logger from '@eventbrite/client-logger';
import { sdkRequest } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { PricingAndPackagingAPIError } from '../error';
import { PricingAndPackagingFeatureFlags } from '../types';
import { sendUpdateToIntercom } from '../utils';
import { PlanOption } from './types';
import { getPlanName } from './utils';

export const fetchOrganizationPlan = async (
    organizationId: string,
    options?: { featureFlags?: PricingAndPackagingFeatureFlags },
): Promise<PlanOption> => {
    if (!organizationId) {
        throw new Error(
            '[fetchOrganizationPlan]: organizationId is a required parameter.',
        );
    }

    try {
        const plan = (await sdkRequest(
            `/api/v3/organizations/${organizationId}/plan/`,
            { method: 'GET' },
        )) as PlanOption;

        // check if the org has a plan before adding the plan_name property
        if (plan?.plan_id) {
            plan.plan_name = getPlanName(plan, options?.featureFlags);
        }

        if (plan?.features) {
            const intercomUpdateObject = {} as any;

            intercomUpdateObject.pp_current_ticket_limit = isNaN(
                //eslint-disable-next-line
                //@ts-ignore
                plan.features.event_capacity,
            )
                ? gettext('unlimited')
                : `${plan.features.event_capacity}`;

            intercomUpdateObject.pp_email_daily_send_limit =
                plan.features.campaign_daily_limit;

            if (plan?.pricing[0]?.period?.interval) {
                intercomUpdateObject.pp_subscription_interval =
                    plan.pricing[0].period.interval;
            }

            sendUpdateToIntercom(intercomUpdateObject);
        }

        return plan;
    } catch (error) {
        logger.error(error, {
            message: `Error in fetchOrganizationPlan for organization ${organizationId}:`,
        });
        throw new PricingAndPackagingAPIError({
            message: `There was an issue fetching the organization's plan. Please try again or contact support for assistance.`,
            statusCode: error.response.status,
            parsedError: error.parsedError,
        });
    }
};

export const fetchPlans = async (options?: {
    featureFlags?: PricingAndPackagingFeatureFlags;
}): Promise<PlanOption[]> => {
    try {
        const organizationId =
            window?.__SERVER_DATA__?.activeOrganizationId ||
            window?.__SERVER_DATA__?.user?.activeOrganization?.id;

        const response = (await sdkRequest(
            `/api/v3/plans/?organizationId=${organizationId}`,
        )) as PlanOption[];
        return response.map((plan) => ({
            ...plan,
            plan_name: getPlanName(plan, options?.featureFlags),
        }));
    } catch (error) {
        logger.error(error, {
            message: 'Error in fetchPlans',
        });

        throw new PricingAndPackagingAPIError({
            message: `There was an issue fetching subscription plans. Please try again or contact support for assistance.`,
            statusCode: error.response.status,
            parsedError: error.parsedError,
        });
    }
};

export * from './types';
export * from './utils';
export * from './__mocks__';
